/**
 * <purchase-options>
 * -----------------------------------------------------------------------------
 * Reusable add-to-cart / discount / subscription component.
 *
 * Replaces the page-specific "starter kit + cartridge subscription" block with a
 * single engine that can serve several user stories by changing one `mode`:
 *
 *   - 'onetime'                 Plain product, single add-to-cart button.
 *   - 'subscription'            One product sold on a Recharge selling plan, with
 *                               a frequency picker. Optional one-time toggle.
 *   - 'bundle'                  Primary product one-time (optionally discounted)
 *                               OR "subscribe & save": primary + a recurring
 *                               secondary product (e.g. starter kit + cartridges).
 *   - 'subscription_plus_bundle' Subscribe to the primary product, OR add a
 *                               one-time add-on alongside the subscription
 *                               (e.g. on the cartridge page: cartridge sub, or
 *                               starter kit + cartridge sub).
 *
 * All pricing, variants and selling plans are derived from Shopify objects in
 * Liquid (see snippets/purchase-options.liquid) and passed in via a JSON config
 * block, so switching purpose/product is a Liquid change, not a JS change.
 *
 * Subscriptions are added with Shopify's native selling plans via the Ajax Cart
 * API (`/cart/add.js` with an `items` array, each item optionally carrying a
 * `selling_plan` id). This is the supported path for Recharge's Shopify Checkout
 * Integration (V2).
 * -----------------------------------------------------------------------------
 */
class PurchaseOptions extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.config = this.#readConfig();
    if (!this.config) return;

    this.root = this.querySelector('[data-purchase-options-root]');
    if (!this.root) return;

    this.products = this.config.products || {};
    this.options = this.#buildOptions();

    if (!this.options.length) return;

    const defaultOption =
      this.options.find((option) => option.key === this.config.defaultOptionKey) ||
      this.options[0];

    this.state = {
      selectedOptionKey: defaultOption.key,
      selectedPlanId: null,
      primaryVariantId: this.#resolvePrimaryVariantId(),
      adding: false,
      error: '',
    };

    this.#syncSelectedPlan();
    this.#render();
    this.#bindVariantTracking();

    this.initialised = true;
  }

  /* ---------------------------------------------------------------------------
   * Config
   * ------------------------------------------------------------------------ */

  #readConfig() {
    const el = this.querySelector('script[type="application/json"][data-purchase-options-config]');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      console.error('[purchase-options] Invalid config JSON', error);
      return null;
    }
  }

  /* ---------------------------------------------------------------------------
   * Variant resolution
   * ------------------------------------------------------------------------ */

  // Primary variant follows the section's variant radios when present, so the
  // component stays in sync with the gallery / variant picker on the page.
  #resolvePrimaryVariantId() {
    const checked = document.querySelector('input[name="variant"]:checked');
    if (checked && checked.value) return Number(checked.value);

    const primary = this.products.primary;
    return primary ? Number(primary.defaultVariantId) : null;
  }

  // The product that carries the selling plans. When it IS the primary product
  // (subscription / subscription_plus_bundle modes) the active plan list and
  // price track the selected variant.
  #activeSubscriptionVariantId() {
    const sub = this.products.subscription;
    if (!sub) return null;

    if (sub.isPrimary && sub.variants && sub.variants[this.state.primaryVariantId]) {
      return this.state.primaryVariantId;
    }
    return Number(sub.defaultVariantId);
  }

  #subscriptionVariantData() {
    const sub = this.products.subscription;
    if (!sub || !sub.variants) return null;

    const id = this.#activeSubscriptionVariantId();
    return sub.variants[id] || sub.variants[sub.defaultVariantId] || null;
  }

  #variantData(source) {
    const product = this.products[source === 'subscription' ? 'subscription' : source];
    if (!product || !product.variants) return null;

    let id;
    if (source === 'primary') id = this.state.primaryVariantId;
    else if (source === 'subscription') id = this.#activeSubscriptionVariantId();
    else id = product.defaultVariantId;

    return product.variants[id] || product.variants[product.defaultVariantId] || null;
  }

  #variantId(source) {
    const product = this.products[source];
    if (!product) return null;

    if (source === 'primary') return this.state.primaryVariantId;
    if (source === 'subscription') return this.#activeSubscriptionVariantId();
    return Number(product.defaultVariantId);
  }

  /* ---------------------------------------------------------------------------
   * Selling plans
   * ------------------------------------------------------------------------ */

  // Normalise a Recharge / Shopify selling plan into a short, sortable frequency.
  // Unknown intervals fall back to a cleaned plan name (so any Recharge setup
  // works, rather than silently dropping plans the old code didn't recognise).
  #normalisePlan(plan) {
    const name = String(plan.name || '').toLowerCase();
    const match = name.match(/(\d+)\s*(day|days|week|weeks|month|months|year|years)/);

    if (match) {
      const n = parseInt(match[1], 10);
      const unitRaw = match[2].replace(/s$/, '');
      const unitDays = { day: 1, week: 7, month: 30, year: 365 }[unitRaw] || 7;
      const plural = n === 1 ? unitRaw : `${unitRaw}s`;

      return {
        key: `${n}-${unitRaw}`,
        label: `${n} ${plural}`,
        sort: n * unitDays,
      };
    }

    if (name.includes('monthly')) {
      return { key: '1-month', label: 'Monthly', sort: 30 };
    }

    // Fallback: strip common Recharge prefixes and keep the plan around.
    const cleaned = String(plan.name || 'Subscription')
      .replace(/^(delivery\s+)?every\s+/i, '')
      .replace(/^subscribe.*?[-–—]\s*/i, '')
      .trim();

    return { key: `plan-${plan.id}`, label: cleaned || 'Subscription', sort: 9999 };
  }

  #activePlans() {
    const subVariant = this.#subscriptionVariantData();
    if (!subVariant || !Array.isArray(subVariant.plans)) return [];

    const byKey = new Map();

    subVariant.plans.forEach((plan) => {
      const meta = this.#normalisePlan(plan);
      if (byKey.has(meta.key)) return;

      byKey.set(meta.key, {
        ...meta,
        sellingPlanId: plan.id,
        name: plan.name,
        price: Number(plan.perDeliveryPrice || plan.price || subVariant.price || 0),
        compareAtPrice: Number(plan.compareAtPrice || subVariant.price || 0),
      });
    });

    return Array.from(byKey.values()).sort((a, b) => a.sort - b.sort);
  }

  #selectedPlan() {
    const plans = this.#activePlans();
    if (!plans.length) return null;

    return (
      plans.find((plan) => String(plan.sellingPlanId) === String(this.state.selectedPlanId)) ||
      plans[0]
    );
  }

  #syncSelectedPlan() {
    const plans = this.#activePlans();
    if (!plans.length) {
      this.state.selectedPlanId = null;
      return;
    }
    const stillValid = plans.some(
      (plan) => String(plan.sellingPlanId) === String(this.state.selectedPlanId)
    );
    if (!stillValid) this.state.selectedPlanId = plans[0].sellingPlanId;
  }

  /* ---------------------------------------------------------------------------
   * Option presets (built from `mode`, or overridden by config.customOptions)
   * ------------------------------------------------------------------------ */

  #buildOptions() {
    if (Array.isArray(this.config.customOptions) && this.config.customOptions.length) {
      return this.config.customOptions;
    }

    const labels = this.config.labels || {};
    const addonQty = Number(this.config.addonQuantity || 1);
    const discount = Number(this.config.bundleDiscountPercent || 0);
    const badge = this.config.badgeText || '';

    // A subscription is a single per-delivery line item, priced at the plan's
    // per-delivery price. It is never multiplied by a pack size / quantity.
    const subItem = (extraProps) => ({
      source: 'subscription',
      quantity: 1,
      subscription: true,
      properties: extraProps || {},
    });

    const oneTimePrimary = (extraProps) => ({
      source: 'primary',
      quantity: 1,
      subscription: false,
      properties: extraProps || {},
    });

    switch (this.config.mode) {
      case 'subscription': {
        const options = [
          {
            key: 'subscription',
            type: 'subscription',
            label: labels.subscription || 'Subscribe & save',
            badge,
            items: [subItem()],
          },
        ];
        if (this.config.allowOneTime) {
          options.push({
            key: 'onetime',
            type: 'onetime',
            label: labels.onetime || 'One-time purchase',
            items: [oneTimePrimary()],
          });
        }
        return options;
      }

case 'bundle': {
  const isPreorder =
    String(this.config.buyButtonText || '').toLowerCase() === 'pre-order';

  if (isPreorder) {
    return [
      {
        key: 'onetime',
        type: 'onetime',
        label: labels.onetime || 'One-time only',
        items: [oneTimePrimary()],
      },
    ];
  }

  return [
    {
      key: 'subscription',
      type: 'bundle',
      label: labels.subscription || 'Subscribe & save',
      badge,
      items: [
        {
          source: 'primary',
          quantity: 1,
          subscription: false,
          discountPercent: discount,
          properties: { '_Bundle source': 'Purchase options' },
        },
        subItem({ '_Paired with': 'Bundle' }),
      ],
    },
    {
      key: 'onetime',
      type: 'onetime',
      label: labels.onetime || 'One-time only',
      items: [oneTimePrimary()],
    },
  ];
}

      case 'subscription_plus_bundle': {
        return [
          {
            key: 'onetime',
            type: 'onetime',
            label: labels.onetime || 'One-time purchase',
            items: [oneTimePrimary()],
          },
          {
            key: 'subscription',
            type: 'subscription',
            label: labels.subscription || 'Subscribe',
            items: [subItem()],
          },
          {
            key: 'bundle',
            type: 'bundle',
            label: labels.bundle || 'Add a starter kit',
            badge,
            items: [
              {
                source: 'addon',
                quantity: addonQty,
                subscription: false,
                discountPercent: discount,
                properties: { '_Bundle source': 'Subscription page' },
              },
              subItem({ '_Paired with': 'Starter kit' }),
            ],
          },
        ];
      }

      case 'onetime':
      default:
        return [
          {
            key: 'onetime',
            type: 'onetime',
            label: labels.onetime || 'Add to cart',
            items: [oneTimePrimary()],
          },
        ];
    }
  }

  #selectedOption() {
    return (
      this.options.find((option) => option.key === this.state.selectedOptionKey) ||
      this.options[0]
    );
  }

  #optionHasSubscription(option) {
    return option.items.some((item) => item.subscription);
  }
  // True when there is no real choice to present: exactly one purchase option
  // exists, and that option carries no subscription line. This is the "no
  // Recharge subscription set up for this product" case (plain `onetime` mode).
  // A single `subscription`-mode option (no one-time toggle) is NOT collapsed,
  // since that's a real subscription product, not a missing-subscription case.
  // Bundle / subscription_plus_bundle modes always produce 2-3 options, so
  // they never hit this path.
  #isSingleNonSubscriptionOption() {
    return this.options.length === 1 && !this.#optionHasSubscription(this.options[0]);
  }
  /* ---------------------------------------------------------------------------
   * Pricing
   * ------------------------------------------------------------------------ */

  // Merchant-editable copy, resolved from config.text with safe defaults.
  // A blank/missing name override falls back to the Shopify product title in
  // #renderLineItem, so the title stays the single source of truth.
  #text() {
    const t = this.config.text || {};
    return {
      perDelivery: t.perDelivery || '/ delivery',
      deliverEvery: t.deliverEvery || ', deliver every:',
      withDiscount: t.withDiscount || ' with discount',
      names: t.names || {},
    };
  }

  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: this.config.currency || 'GBP',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  #itemUnitPrices(item) {
    if (item.subscription) {
      const plan = this.#selectedPlan();
      const subVariant = this.#subscriptionVariantData();
      const base = subVariant ? Number(subVariant.price || 0) : 0;
      return {
        price: plan ? plan.price : base,
        original: plan ? Math.max(plan.compareAtPrice, plan.price) : base,
      };
    }

    const variant = this.#variantData(item.source);
    const base = variant ? Number(variant.price || 0) : 0;
    const discount = Number(item.discountPercent || 0);
    const discounted = discount ? Math.round(base * ((100 - discount) / 100)) : base;

    return { price: discounted, original: base };
  }

  #optionTotals(option) {
    return option.items.reduce(
      (acc, item) => {
        const { price, original } = this.#itemUnitPrices(item);
        // Subscription lines are always per-delivery — never multiplied.
        const qty = item.subscription ? 1 : item.quantity;
        acc.price += price * qty;
        acc.original += original * qty;
        return acc;
      },
      { price: 0, original: 0 }
    );
  }

  /* ---------------------------------------------------------------------------
   * Rendering
   * ------------------------------------------------------------------------ */

  #render() {
    const selected = this.#selectedOption();
    const buyable = this.config.purchasable !== false;
    const singleNonSub = this.#isSingleNonSubscriptionOption();


     this.root.innerHTML = `
      <div class="purchase-options-widget">
        ${
          singleNonSub
            ? ''
            : `<div class="po-options">
                 ${this.options.map((option) => this.#renderOption(option)).join('')}
               </div>`
        }

        <button
          type="button"
          class="nimbi-button po-add"
          data-po-add
          ${this.state.adding || !buyable ? 'disabled' : ''}
        >
        <span data-po-button-label>${
  this.state.adding
    ? 'Adding'
    : (
        (
          window.location.pathname.includes('/products/nimbi-body-sculpt-shaving-starter-kit-with-rich-slip-oil') ||
          window.location.pathname.includes('/products/nimbi-body-sculpt-shaving-starter-kit-with-buffer-butter')
        ) &&
        String(this.config.currency || '').toUpperCase() === 'GBP'
      )
      ? 'Pre-order'
      : this.config.buyButtonText
}</span>
          ${buyable ? '<span>&nbsp;-&nbsp;</span>' : ''}
          ${buyable ? `<span class="nimbi-button-price">${this.#renderTotal(selected)}</span>` : ''}
        </button>
${String(this.config.buyButtonText || '').toLowerCase() === 'pre-order' &&
  !window.location.pathname.includes('/products/nimbi-shave-duo')
  ? `
    <p class="po-preorder-note">
      Pre-order now and save 35%. Estimated shipping from mid-September. If you order other in-stock Nimbi items, they’ll head your way first, with Body Sculpt following separately.
    </p>
  `
  : ''
}
        <p class="text-prod-1-mono-1 po-error" data-po-error
           style="${this.state.error ? '' : 'display:none;'} margin:0;">
          ${this.state.error || ''}
        </p>
      </div>
    `;

    this.#bindEvents();
  }

  #renderOption(option) {
    const isSelected = option.key === this.state.selectedOptionKey;
    const hasSub = this.#optionHasSubscription(option);
    const totals = this.#optionTotals(option);

    const badge = option.badge
      ? `<span class="po-badge">${option.badge}</span>`
      : '';

    // Selected, subscription-bearing options expand to show a line-item
    // breakdown plus the frequency picker.
    const expanded = isSelected && hasSub;

    return `
      <div class="po-option ${isSelected ? 'is-selected' : ''}" data-po-option="${option.key}">
        ${badge}
        <label class="po-label ${expanded ? 'po-label-heading' : ''}">
          <span class="po-selector">
            <input
              type="radio"
              name="po_option"
              value="${option.key}"
              class="po-input"
              data-po-option-input
              ${isSelected ? 'checked' : ''}
            >
            <span class="po-radio" aria-hidden="true"></span>
            ${option.label}
          </span>
          ${
            expanded
              ? ''
              : `<span class="po-prices">${this.#renderTotal(option)}</span>`
          }
        </label>
        ${expanded ? this.#renderDetails(option) : ''}
      </div>
    `;
  }

#renderTotal(option) {
  const totals = this.#optionTotals(option);

  const isUKShaveDuo =
  (
    window.location.pathname.includes('/products/nimbi-body-sculpt-shaving-starter-kit-with-rich-slip-oil') ||
    window.location.pathname.includes('/products/nimbi-shave-duo-with-buffer-butter')
  ) &&
  String(this.config.currency || '').toUpperCase() === 'GBP';

const isPreorder =
  String(this.config.buyButtonText || '').toLowerCase() === 'pre-order' ||
  isUKShaveDuo;

  if (isPreorder && option.type === 'onetime') {
const isUSShaveDuo =
  (
    window.location.pathname === '/products/nimbi-shave-duo' ||
    window.location.pathname === '/products/nimbi-shave-duo-with-buffer-butter-1'
  ) &&
  String(this.config.currency || '').toUpperCase() === 'USD';


const preorderPrice = isUSShaveDuo
  ? 2584
  : isUKShaveDuo
    ? 2369
    : Math.round(totals.price * 0.65);

    return `
      <s class="po-muted">${this.#formatMoney(totals.price)}</s>
      <span>${this.#formatMoney(preorderPrice)}</span>
    `;
  }

  if (totals.original > totals.price) {
    return `
      <s class="po-muted">${this.#formatMoney(totals.original)}</s>
      <span>${this.#formatMoney(totals.price)}</span>
    `;
  }

  return `<span>${this.#formatMoney(totals.price)}</span>`;
}

  #renderDetails(option) {
    const lines = option.items.map((item) => this.#renderLineItem(item)).join('');
    return `
      <div class="po-details">
        ${lines}
        ${this.#renderFrequencyPicker()}
      </div>
    `;
  }

  #renderLineItem(item) {
    const { price, original } = this.#itemUnitPrices(item);
    const text = this.#text();

    if (item.subscription) {
      const subProduct = this.products.subscription || {};
      const name = text.names.subscription || subProduct.title || 'Subscription';
      const label = `${name}${text.deliverEvery}`;

      return `
        <div class="po-line">
          <span>${label}</span>
          <span class="po-line-price">${this.#formatMoney(price)} ${text.perDelivery}</span>
        </div>
      `;
    }

    const isAddon = item.source === 'addon';
    const product = isAddon ? this.products.addon : this.products.primary;
    const nameOverride = isAddon ? text.names.addon : text.names.primary;
    const name = nameOverride || (product && product.title) || 'Item';
    const suffix = item.discountPercent ? text.withDiscount : '';
    const priceMarkup =
      original > price
        ? `<span class="visually-hidden">Was:</span>
           <span class="po-price po-muted po-strike">${this.#formatMoney(original)}</span>
           <span class="po-price">${this.#formatMoney(price)}</span>`
        : `<span class="po-price">${this.#formatMoney(price)}</span>`;

    return `
      <div class="po-line">
        <span>${name}${suffix}</span>
        <span class="po-line-price">${priceMarkup}</span>
      </div>
    `;
  }

  #renderFrequencyPicker() {
    const plans = this.#activePlans();

    if (!plans.length) {
      return `
        <p class="text-prod-1-mono-1" style="margin:0;">
          Subscription frequencies are unavailable. Check the subscription product
          handle and that Recharge selling plans are published.
        </p>
      `;
    }

    return `
      <div class="po-plan-list" data-po-plan-list>
        ${plans
          .map(
            (plan) => `
          <label class="po-plan ${
            String(plan.sellingPlanId) === String(this.state.selectedPlanId)
              ? 'is-selected'
              : ''
          }" data-po-plan>
            <input
              type="radio"
              name="po_selling_plan"
              value="${plan.sellingPlanId}"
              data-plan-label="${plan.label}"
              data-po-plan-input
              ${String(plan.sellingPlanId) === String(this.state.selectedPlanId) ? 'checked' : ''}
            >
            <span>${plan.label}</span>
          </label>
        `
          )
          .join('')}
      </div>
    `;
  }

  /* ---------------------------------------------------------------------------
   * Events
   * ------------------------------------------------------------------------ */

  #bindEvents() {
    this.root.querySelectorAll('[data-po-option-input]').forEach((input) => {
      input.addEventListener('change', () => this.#selectOption(input.value));
    });

    // Clicking anywhere on a card (not on a plan chip) selects it.
    this.root.querySelectorAll('[data-po-option]').forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('[data-po-plan]')) return;
        this.#selectOption(card.dataset.poOption);
      });
    });

    this.root.querySelectorAll('[data-po-plan-input]').forEach((input) => {
      input.addEventListener('change', () => {
        this.state.selectedPlanId = input.value;
        this.#render();
      });
    });

    const addButton = this.root.querySelector('[data-po-add]');
    if (addButton) {
      addButton.addEventListener('click', () => this.#addToCart());
    }
  }

  #selectOption(key) {
    if (this.state.selectedOptionKey === key) return;
    this.state.selectedOptionKey = key;
    this.state.error = '';
    this.#syncSelectedPlan();
    this.#render();
  }

  // Keep the primary variant in sync with the page's variant selector.
  #bindVariantTracking() {
    document.addEventListener('change', (event) => {
      if (event.target && event.target.name === 'variant' && event.target.checked) {
        this.state.primaryVariantId = Number(event.target.value);
        this.#syncSelectedPlan();
        this.#render();
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * Add to cart
   * ------------------------------------------------------------------------ */

  #buildCartItems(option) {
    const plan = this.#selectedPlan();

    return option.items.map((item) => {
      const id = this.#variantId(item.source);
      const cartItem = {
        id: Number(id),
        quantity: item.subscription ? 1 : Number(item.quantity || 1),
        properties: { ...(item.properties || {}) },
      };

      if (item.subscription && plan) {
        cartItem.selling_plan = Number(plan.sellingPlanId);
        cartItem.properties['Refill frequency'] = plan.label;
      }

      return cartItem;
    });
  }

  async #addToCart() {
    const option = this.#selectedOption();

    if (this.#optionHasSubscription(option) && !this.#selectedPlan()) {
      this.state.error = 'Please choose a delivery frequency.';
      this.#render();
      return;
    }

    const items = this.#buildCartItems(option);
    if (items.some((item) => !item.id)) {
      this.state.error = 'This option is not available right now.';
      this.#render();
      return;
    }
    // Append any available free gifts to the same cart-add request. These are
  // read from globals set by the product section (window.availableFreeGifts /
  // window.freeGiftDiscountCode). Free gifts are always one-time, quantity 1.
const freeGiftIds = Array.isArray(window.availableFreeGifts)
  ? window.availableFreeGifts
  : [];

freeGiftIds.forEach((giftId) => {
  items.push({
    id: Number(giftId),
    quantity: 1,
    properties: { '_Free gift': 'true' },
  });
});


    this.state.adding = true;
    this.state.error = '';
    this.#render();

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Unable to add to cart.');
      }

      const cart = await response.json().catch(() => null);

      // Apply the free-gift discount code (if any). Non-fatal.
if (freeGiftIds.length && window.freeGiftDiscountCode) {
  try {
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount: window.freeGiftDiscountCode }),
    });
  } catch (discountError) {
    console.warn('[purchase-options] Failed to apply discount code', discountError);
  }
}

      // Let cart drawers / pixels react; default behaviour is a redirect to /cart.
      this.dispatchEvent(
        new CustomEvent('purchase-options:added', {
          bubbles: true,
          detail: { items, cart, option: option.key },
        })
      );

      if (this.config.cartRedirect !== false) {
        window.location.href = `${root}cart`;
        return;
      }
    } catch (error) {
      this.state.error = error.message || 'Unable to add to cart.';
    } finally {
      this.state.adding = false;
      this.#render();
    }
  }
}

if (!customElements.get('purchase-options')) {
  customElements.define('purchase-options', PurchaseOptions);
}